import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// Import Academic Calendar Module
import AcademicCalendarModule from './components/Calendar/AcademicCalendarModule';

// Import Teacher Directory Module
import TeacherDirectoryModule from './components/TeacherDirectoryModule/TeacherDirectoryModule';

// Import Main Page Module
import MainPage from './components/Core/MainPage';

// Import Attendance Page Module
import AttendancePage from './components/Attendance/AttendancePage';

// Import School Event Page Module
import SchoolEventPage from './components/School/SchoolEventPage';

// Import Student Grades Page Module
import StudentGradesPage from './components/Student/StudentGradesPage';

// Import Payment Records Module
import PaymentRecordsModule from './components/Payment/PaymentRecordsModule';

// Import News and Notification Module
import NewsAndNotificationModule from './components/NewsAndNotification/NewsAndNotificationModule';

// Import SLP Management Page
import SLPManagementPage from './components/SLP/SLPManagementPage';

// Import SLP Event Page
import SLPEventPage from './components/SLP/SLPEventPage';

// Import SLP Event Details
import SLPEventDetails from './components/SLP/SLPEventDetails';

// Import SLP Event Form
import SLPEventForm from './components/SLP/SLPEventForm';

// Import SLP Student Selector
import SLPStudentSelector from './components/SLP/SLPStudentSelector';

// Import Class Attendance Detail
import ClassAttendanceDetail from './components/Student/ClassAttendanceDetail';

// Import School Info Page
import SchoolInfoPage from './components/School/SchoolInfoPage';

// Import Supabase Auth
import SupabaseAuth from './components/Core/SupabaseAuth';

// Import Credits Panel
import CreditsPanel from './components/Core/CreditsPanel';

// Import Auth Sim
import AuthSim from './components/Core/AuthSim';

// Import ECA Page
import ECAPage from './components/ECA/ECAPage';

// Import new Information Pages
import TeacherInfoPage from './components/TeacherInfo';
import StudentInformationDashboard from './components/StudentInfo/StudentInformationDashboard';
import ClassInfoPage from './components/ClassInfo';
import StaffPage from './components/Staff/StaffPage';

// Import Group Messages Module
import GroupMessagesModule from './components/GroupMessages/GroupMessagesModule';

// Import Sports Days Module
import SportsDaysModule from './components/SportsDays/SportsDaysModule';

// Import Timetable Module
import TimetableModule from './components/Timetable/TimetableModule';

// Import School Timetable Settings
import SchoolTimetableSettings from './components/Settings/SchoolTimetableSettings';

// Import MyClass Timetable Planner Module
import MyClassTimetablePlanner from './components/TimetablePlanner/MyClassTimetablePlanner';

// Import Lunchbox Module
import LunchboxModule from './components/Lunchbox/LunchboxModule';

// Import Lesson Plan Module
import LessonPlanModule from './components/LessonPlan/LessonPlanModule';

// Import Homework Module
import HomeworkModule from './components/Homework/HomeworkModule';

// Import SLP Module
import SlpModule from './components/SLP/SlpModule';

// Import SDR Module
import SdrModule from './components/SDR/SdrModule';

// Import eNotice Module
import ENoticeModule from './components/eNotice';
// Import Library Module
import LibraryModule from './components/Library';

// Import App Store Module
import AppStoreHome from './components/AppStore/AppStoreHome';
import EnhancedAppStore from './components/AppStore/EnhancedAppStore';
import AppDetails from './components/AppStore/AppDetails';
import DeveloperPortal from './components/AppStore/DeveloperPortal';
import AdminReviewSystem from './components/AppStore/AdminReviewSystem';

// Import Billing Module
import BillingDashboard from './components/Billing/BillingDashboard';

// Import Admin Modules
import UserManagementModule from './components/Admin/UserManagementModule';
import SystemSettingsModule from './components/Admin/SystemSettingsModule';
import AnalyticsModule from './components/Admin/AnalyticsModule';

// Import grades data
import { gradesData } from './Data/gradesData';

// Import mock data
import { mockAttendanceData } from './mockData/attendanceData';
import { mockPaymentData } from './mockData/paymentData';
import { mockNewsData } from './mockData/newsData';
import { mockSchoolEvents } from './mockData/schoolEvents';
import { mockTeachers } from './mockData/teacherData';
import { mockStudents } from './mockData/studentData';

// Import helper functions
import { calculateAttendanceRate, getAttendanceStatus } from './lib/attendanceHelpers';
import { formatCurrency, calculateTotalAmount } from './lib/paymentHelpers';
import { formatDate, getCurrentDate } from './lib/dateHelpers';

function App() {
  // Main application state
  const [activeTab, setActiveTab] = useState('main');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar starts hidden by default
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedAppId, setSelectedAppId] = useState(null); // State for selected app in AppDetails
  const lastToggleTime = useRef(0); // Track last toggle time to prevent rapid clicking

  // Dummy state for demonstration
  const [attendanceData, setAttendanceData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [schoolEvents, setSchoolEvents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);

  // Initialize data on component mount
  useEffect(() => {
    // Simulate data loading
    setIsLoading(true);
    
    // Load mock data
    setAttendanceData(mockAttendanceData);
    setPaymentData(mockPaymentData);
    setNewsData(mockNewsData);
    setSchoolEvents(mockSchoolEvents);
    setTeachers(mockTeachers);
    setStudents(mockStudents);
    
    // Simulate authentication check
    setTimeout(() => {
      setIsAuthenticated(true);
      setCurrentUser({
        id: 'f6f781a2-dde8-489c-86f9-adbf1f498dde',
        name: 'Student A',
        role: 'student',
        class: 'S1A'
      });
      setIsLoading(false);
    }, 1000);

    // Handle window resize for responsive sidebar behavior
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Always hide sidebar on mobile, but don't auto-show on desktop
      if (mobile) {
        setIsSidebarOpen(false);
      }
      // Desktop: keep current sidebar state (don't auto-show)
    };

    // Set initial sidebar state based on screen size
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle sidebar toggle with proper event handling
  const handleSidebarToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent rapid clicking by checking time between clicks
    const now = Date.now();
    if (now - lastToggleTime.current < 300) { // 300ms debounce
      return;
    }
    lastToggleTime.current = now;
    
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Handle tab changes
  const handleTabChange = (tabName, sourceInfo = null) => {
    console.log('App: Tab change requested:', tabName, 'with source:', sourceInfo);
    
    // Always hide sidebar after navigation (both mobile and desktop)
    setIsSidebarOpen(false);
    
    setActiveTab(tabName);
  };

  // Handle back navigation
  const handleBack = () => {
    // Always hide sidebar when navigating back (both mobile and desktop)
    setIsSidebarOpen(false);
    
    setActiveTab('main');
  };

  // Render main content based on active tab
  const renderMainContent = () => {
    switch (activeTab) {
      case 'main':
        return (
          <MainPage
            currentUser={currentUser}
            attendanceData={attendanceData}
            paymentData={paymentData}
            newsData={newsData}
            schoolEvents={schoolEvents}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        );
      
      case 'attendance':
        return (
          <AttendancePage
            attendanceData={attendanceData}
            onBack={handleBack}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
          />
        );
      
      case 'school-event':
        return (
          <SchoolEventPage
            schoolEvents={schoolEvents}
            onBack={handleBack}
            onTabChange={handleTabChange}
            formatDate={formatDate}
          />
        );
      
      case 'teacher-directory':
        return (
          <TeacherDirectoryModule
            teachers={teachers}
            onBack={handleBack}
            onTabChange={handleTabChange}
            onSidebarClose={() => {
              console.log('🔄 App: Closing sidebar from TeacherDirectoryModule');
              setIsSidebarOpen(false);
            }}
          />
        );
      
      case 'grades':
        return (
          <StudentGradesPage
            gradesData={gradesData}
            onBack={handleBack}
            formatDate={formatDate}
          />
        );
      
      case 'payment':
        return (
          <PaymentRecordsModule
            paymentData={paymentData}
            onBack={handleBack}
            onTabChange={handleTabChange}
            formatCurrency={formatCurrency}
            calculateTotalAmount={calculateTotalAmount}
            formatDate={formatDate}
          />
        );
      
      case 'news':
        return (
          <NewsAndNotificationModule
            newsData={newsData}
            onBack={handleBack}
            formatDate={formatDate}
          />
        );
      
      case 'eca':
        return (
          <ECAPage onTabChange={handleTabChange} />
        );
      
      case 'slp-events':
        return (
          <SLPEventPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp-event-details':
        return (
          <SLPEventDetails
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp-event-form':
        return (
          <SLPEventForm
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp-student-selector':
        return (
          <SLPStudentSelector
            students={students}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'class-attendance':
        return (
          <ClassAttendanceDetail
            attendanceData={attendanceData}
            onBack={handleBack}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
          />
        );
      
      case 'academic-calendar':
        return (
          <AcademicCalendarModule
            onBack={handleBack}
          />
        );
      
      case 'school-info':
        return (
          <SchoolInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'teacher-info':
        return (
          <TeacherInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'student-info':
        return (
          <StudentInformationDashboard
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'class-info':
        return (
          <ClassInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'staff':
        return (
          <StaffPage
            onBack={handleBack}
          />
        );
      
      case 'group-messages':
        return (
          <GroupMessagesModule currentUser={currentUser} />
        );
      
      case 'sports-days':
        return (
          <SportsDaysModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'timetable':
        return (
          <TimetableModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'timetable-settings':
        return (
          <SchoolTimetableSettings
            tenantId="eadcd263-a399-4261-8cbd-8fb1ce6da0e3"
            schoolName="MyClass School"
            onBack={handleBack}
          />
        );
      
      case 'timetable-planner':
        return (
          <MyClassTimetablePlanner
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'lunchbox':
        return (
          <LunchboxModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'lesson-plan':
        return (
          <LessonPlanModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'homework':
        return (
          <HomeworkModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp':
        return (
          <SlpModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'sdr':
        return (
          <SdrModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'e-notices':
                return (
                  <ENoticeModule
                    currentUser={currentUser}
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
              case 'library':
                return (
                  <LibraryModule
                    currentUser={currentUser}
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
              
              case 'app-store':
                return (
                  <EnhancedAppStore
                    currentTenant="tn_01"
                    currentUser={currentUser}
                  />
                );
              case 'app-details':
                return (
                  <AppDetails
                    appId={selectedAppId}
                    currentTenant="tn_01"
                    currentUser={currentUser}
                    onBack={() => setActiveTab('app-store')}
                  />
                );
              case 'developer-portal':
                return (
                  <DeveloperPortal
                    currentTenant="tn_01"
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
              case 'admin-review':
                return (
                  <AdminReviewSystem
                    currentTenant="tn_01"
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
              case 'billing-dashboard':
                return (
                  <BillingDashboard
                    currentTenant="tn_01"
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
      
      // User Management
      case 'users':
        return (
          <UserManagementModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'students':
        return (
          <StudentInformationDashboard
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            adminMode={true}
          />
        );

      case 'teachers':
        return (
          <TeacherInfoPage
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            adminMode={true}
          />
        );

      case 'parents':
        return (
          <div style={{ padding: '20px' }}>
            <h2>家長管理</h2>
            <p>Parent Management Module - Coming Soon</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'classes':
        return (
          <ClassInfoPage
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            adminMode={true}
          />
        );

      // Academic Configuration
      case 'academic-years':
        return (
          <div style={{ padding: '20px' }}>
            <h2>學年管理</h2>
            <p>Academic Year Management Module - Coming Soon</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'subjects':
        return (
          <div style={{ padding: '20px' }}>
            <h2>科目管理</h2>
            <p>Subject Management Module - Coming Soon</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      // System
      case 'system-settings':
        return (
          <SystemSettingsModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'analytics':
        return (
          <AnalyticsModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'data-management':
        return (
          <div style={{ padding: '20px' }}>
            <h2>資料管理</h2>
            <p>Data Management Module - Coming Soon</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'audit-logs':
        return (
          <div style={{ padding: '20px' }}>
            <h2>審計日誌</h2>
            <p>Audit Logs Module - Coming Soon</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );
      
      default:
        return (
          <MainPage
            currentUser={currentUser}
            attendanceData={attendanceData}
            paymentData={paymentData}
            newsData={newsData}
            schoolEvents={schoolEvents}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        );
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }

  // Show authentication if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <SupabaseAuth
          onLogin={(user) => {
            setIsAuthenticated(true);
            setCurrentUser(user);
          }}
        />
        <AuthSim
          onLogin={(user) => {
            setIsAuthenticated(true);
            setCurrentUser(user);
          }}
        />
      </div>
    );
  }

  // Main application render
  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <button 
            className={`sidebar-toggle ${isSidebarOpen ? 'active' : ''}`}
            onClick={handleSidebarToggle}
            aria-label="Toggle sidebar"
            title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          >
            {isSidebarOpen ? '✕' : '☰'}
          </button>
          <h1>MyClassAdmin</h1>
          <div className="user-info">
            <span>Welcome, {currentUser?.name}</span>
            <button 
              className="logout-btn"
              onClick={() => setIsAuthenticated(false)}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Backdrop */}
      {isSidebarOpen && isMobile && (
        <div 
          className="sidebar-backdrop"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsSidebarOpen(false);
          }}
        />
      )}

      {/* Sidebar Navigation */}
      <div 
        className={`app-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <nav className="sidebar-navigation">
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'main' ? 'active' : ''}`}
              onClick={() => handleTabChange('main')}
            >
              🏠 主頁
            </button>
          </div>
          
          {/* User Management */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>👥 用戶管理</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => handleTabChange('users')}
            >
              👤 用戶管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'students' ? 'active' : ''}`}
              onClick={() => handleTabChange('students')}
            >
              👨‍🎓 學生管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'teachers' ? 'active' : ''}`}
              onClick={() => handleTabChange('teachers')}
            >
              👨‍🏫 教師管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'parents' ? 'active' : ''}`}
              onClick={() => handleTabChange('parents')}
            >
              👨‍👩‍👧 家長管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'staff' ? 'active' : ''}`}
              onClick={() => handleTabChange('staff')}
            >
              👷 職員管理
            </button>
          </div>
          
          {/* Class Management */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📚 班級管理</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'classes' ? 'active' : ''}`}
              onClick={() => handleTabChange('classes')}
            >
              🏫 班級管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'class-info' ? 'active' : ''}`}
              onClick={() => handleTabChange('class-info')}
            >
              ℹ️ 班級資訊
            </button>
          </div>
          
          {/* Academic Configuration */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📖 學術配置</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'academic-years' ? 'active' : ''}`}
              onClick={() => handleTabChange('academic-years')}
            >
              📅 學年管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'subjects' ? 'active' : ''}`}
              onClick={() => handleTabChange('subjects')}
            >
              📚 科目管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'timetable-settings' ? 'active' : ''}`}
              onClick={() => handleTabChange('timetable-settings')}
            >
              ⚙️ 時間表設定
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'timetable-planner' ? 'active' : ''}`}
              onClick={() => handleTabChange('timetable-planner')}
            >
              📊 時間表編排
            </button>
          </div>
          
          {/* Academic Operations */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📊 學術操作</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'grades' ? 'active' : ''}`}
              onClick={() => handleTabChange('grades')}
            >
              📈 成績管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'homework' ? 'active' : ''}`}
              onClick={() => handleTabChange('homework')}
            >
              📝 家課管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'attendance' ? 'active' : ''}`}
              onClick={() => handleTabChange('attendance')}
            >
              📊 考勤管理
            </button>
          </div>
          
          {/* Communication */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>💬 通訊</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'group-messages' ? 'active' : ''}`}
              onClick={() => handleTabChange('group-messages')}
            >
              💬 小組訊息
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'e-notices' ? 'active' : ''}`}
              onClick={() => handleTabChange('e-notices')}
            >
              📋 電子通告
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'news' ? 'active' : ''}`}
              onClick={() => handleTabChange('news')}
            >
              📰 學校宣佈
            </button>
          </div>
          
          {/* Calendar & Events */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📅 日曆活動</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'academic-calendar' ? 'active' : ''}`}
              onClick={() => handleTabChange('academic-calendar')}
            >
              📅 校曆表
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'school-event' ? 'active' : ''}`}
              onClick={() => handleTabChange('school-event')}
            >
              🎉 學校活動
            </button>
          </div>
          
          {/* Resources */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📚 資源</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'library' ? 'active' : ''}`}
              onClick={() => handleTabChange('library')}
            >
              📚 圖書館
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'eca' ? 'active' : ''}`}
              onClick={() => handleTabChange('eca')}
            >
              🏃 課外活動
            </button>
          </div>
          
          {/* System */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>⚙️ 系統</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'system-settings' ? 'active' : ''}`}
              onClick={() => handleTabChange('system-settings')}
            >
              ⚙️ 系統設定
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => handleTabChange('analytics')}
            >
              📊 分析報表
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'data-management' ? 'active' : ''}`}
              onClick={() => handleTabChange('data-management')}
            >
              💾 資料管理
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'audit-logs' ? 'active' : ''}`}
              onClick={() => handleTabChange('audit-logs')}
            >
              📋 審計日誌
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content Area */}
      <main className={`app-main ${isSidebarOpen ? 'with-sidebar' : 'without-sidebar'}`}>
        {renderMainContent()}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <CreditsPanel />
      </footer>
    </div>
  );
}

export default App;
